import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import type { PickingItem } from '../../../services/pickingVentaService';
import { formatCantidadPicking } from '../pickingHelpers';

type PickingTab = 'pendientes' | 'pickeadas' | 'pedidas';

interface PickingItemsTableProps {
  items: PickingItem[];
  ultimoCodigo?: string | null;
  disabled?: boolean;
  onDeshacer: (item: PickingItem) => void;
}

const headSx = { fontWeight: 600, bgcolor: '#f5f5f5' };

const PickingItemsTable: React.FC<PickingItemsTableProps> = ({ items, ultimoCodigo, disabled, onDeshacer }) => {
  const [tab, setTab] = useState<PickingTab>('pendientes');

  const conteos = useMemo(
    () => ({
      pendientes: items.filter((i) => i.cantidadPendiente > 0).length,
      pickeadas: items.filter((i) => i.cantidadPickeada > 0).length,
      pedidas: items.length,
    }),
    [items],
  );

  const visibles = useMemo(() => {
    if (tab === 'pendientes') return items.filter((i) => i.cantidadPendiente > 0);
    if (tab === 'pickeadas') return items.filter((i) => i.cantidadPickeada > 0);
    return items;
  }, [items, tab]);

  const vacioTexto =
    tab === 'pendientes'
      ? 'No quedan artículos pendientes. Presioná F2 para dar el pedido por listo.'
      : tab === 'pickeadas'
        ? 'Todavía no se pickeó ningún artículo.'
        : 'El pedido no tiene artículos.';

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={tab}
        onChange={(_, v: PickingTab) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
      >
        <Tab value="pendientes" label={`Cant. Pendientes (${conteos.pendientes})`} />
        <Tab value="pickeadas" label={`Cant. Pickeadas (${conteos.pickeadas})`} />
        <Tab value="pedidas" label={`Cant. Pedidas (${conteos.pedidas})`} />
      </Tabs>
      <TableContainer sx={{ maxHeight: { xs: 420, lg: 'none' }, flex: { lg: 1 }, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Código</TableCell>
              <TableCell sx={headSx}>Descripción</TableCell>
              <TableCell sx={headSx}>Cód. barras</TableCell>
              <TableCell sx={headSx}>UM</TableCell>
              <TableCell sx={headSx} align="right">Pedida</TableCell>
              <TableCell sx={headSx} align="right">Pickeada</TableCell>
              <TableCell sx={headSx} align="right">Pendiente</TableCell>
              <TableCell sx={headSx} align="center" width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {vacioTexto}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibles.map((item) => {
                const completo = item.cantidadPendiente <= 0;
                const esUltimo = Boolean(ultimoCodigo) && item.codigoArticulo === ultimoCodigo;
                return (
                  <TableRow
                    key={item.nroItem}
                    sx={{
                      bgcolor: esUltimo ? '#FFF8E1' : completo ? '#E8F5E9' : undefined,
                      transition: 'background-color 0.3s',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{item.codigoArticulo}</TableCell>
                    <TableCell>{item.descripcion}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{item.codBarra || '-'}</TableCell>
                    <TableCell>{item.um || '-'}</TableCell>
                    <TableCell align="right">{formatCantidadPicking(item.cantidadPedida)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: completo ? 'success.main' : undefined }}>
                      {formatCantidadPicking(item.cantidadPickeada)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, color: item.cantidadPendiente > 0 ? 'error.main' : 'text.secondary' }}
                    >
                      {formatCantidadPicking(item.cantidadPendiente)}
                    </TableCell>
                    <TableCell align="center">
                      {item.cantidadPickeada > 0 && (
                        <Tooltip title="Deshacer lectura">
                          <span>
                            <IconButton size="small" disabled={disabled} onClick={() => onDeshacer(item)}>
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PickingItemsTable;
